// ============================================================
// store/stash-card-detail/createStashCardDetailStore.ts
//
// Zustand-style store factory for the StashCard detail view.
// Mirrors the pattern used by `createStashScopedStore` so the
// codebase stays uniform: pure state factory + selector hook.
// ============================================================

import { create } from 'zustand';
import type {
  StashCard,
  StashCardActivity,
  StashCardNote,
  Stasher,
  StashCategory,
  StashCardDetailStore,
} from './types';

interface CreateDetailStoreInput {
  card: StashCard;
  stashers: Stasher[];
  categories: StashCategory[];
  currentUserStasherId: string;
  /**
   * Optional side-channel for new activities. The store appends to the
   * card's local activity list AND emits here so the caller can persist
   * to Firestore. When omitted, mutations are local-only.
   */
  onLogActivity?: (activity: StashCardActivity) => void;
  onAddNote?: (note: StashCardNote) => void;
}

/**
 * Creates a fresh zustand store for one StashCard detail view. Components
 * receive this store via `<StashCardDetailStoreProvider>` and subscribe via
 * `useStashCardDetailStore(selector)`.
 */
export function createStashCardDetailStore(input: CreateDetailStoreInput) {
  return create<StashCardDetailStore>((set, get) => ({
    // ───── Initial state ─────
    card: input.card,
    stashers: input.stashers,
    categories: input.categories,
    currentUserStasherId: input.currentUserStasherId,
    loading: false,

    // ───── Actions ─────
    initialize: ({ card, stashers, categories, currentUserStasherId }) => {
      set({ card, stashers, categories, currentUserStasherId, loading: false });
    },

    logActivity: (partial) => {
      const current = get().card;
      if (!current) return;
      const activity: StashCardActivity = {
        ...partial,
        id: `act_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        createdOn: new Date(),
      };
      const updated: StashCard = {
        ...current,
        stashCardActivities: [...(current.stashCardActivities ?? []), activity],
      };
      set({ card: updated });
      input.onLogActivity?.(activity);
    },

    addNote: (partial) => {
      const current = get().card;
      if (!current) return;
      const note: StashCardNote = {
        ...partial,
        recordID: `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        createdOn: new Date(),
      };
      const updated: StashCard = {
        ...current,
        stashCardNotes: [...(current.stashCardNotes ?? []), note],
      };
      set({ card: updated });
      input.onAddNote?.(note);
    },

    reset: () => {
      set({
        card: input.card,
        stashers: input.stashers,
        categories: input.categories,
        currentUserStasherId: input.currentUserStasherId,
        loading: false,
      });
    },
  }));
}

export type StashCardDetailStoreApi = ReturnType<typeof createStashCardDetailStore>;
